import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getLeaderboard } from '../services/api';
import ArchetypeBadge from './ArchetypeBadge';

const GENRES = [
  { id: 'all',     label: 'All' },
  { id: 'general', label: 'General' },
  { id: 'fps',     label: 'FPS' },
  { id: 'moba',    label: 'MOBA' },
];

const SORT_OPTIONS = [
  { id: 'overallScore',    label: 'Overall' },
  { id: 'rawSpeed',        label: 'Reaction' },
  { id: 'aimPrecision',    label: 'Aim' },
  { id: 'trackingAccuracy',label: 'Tracking' },
  { id: 'workingMemory',   label: 'Memory' },
  { id: 'decisionQuality', label: 'Decision' },
  { id: 'consistency',     label: 'Consistency' },
  { id: 'flickSpeed',      label: 'Flick' },
  { id: 'prioritization',  label: 'Prioritization' },
];

const RANK_COLORS = {
  1: '#f59e0b',
  2: '#94a3b8',
  3: '#cd7f32',
};

function ScoreBar({ value }) {
  if (value == null) return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>;
  const color = value >= 80 ? '#10b981' : value >= 60 ? '#00d4ff' : value >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 64, height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${value}%`, height: '100%', background: color, borderRadius: 2, transition: 'width 0.4s ease' }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums', minWidth: 24 }}>{value}</span>
    </div>
  );
}

export default function Leaderboard() {
  const [genre, setGenre] = useState('all');
  const [sortBy, setSortBy] = useState('overallScore');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [archetypeFilter, setArchetypeFilter] = useState('');

  useEffect(() => {
    document.title = 'Leaderboard — Top Cognitive Profiles | NeuroRank';
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { entries: data } = await getLeaderboard({ genre, sortBy, limit: 10 });
      setEntries(data);
    } catch {
      setError('Failed to load leaderboard.');
    } finally {
      setLoading(false);
    }
  }, [genre, sortBy]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { import('../services/track').then(({ track }) => track('leaderboard_view', { genre, sortBy })); }, [genre, sortBy]);

  const sortLabel = SORT_OPTIONS.find((s) => s.id === sortBy)?.label || 'Overall';
  const archetypes = [...new Set(entries.map((e) => e.archetype?.name).filter(Boolean))].sort();
  const filtered = entries.filter((e) => {
    if (search.trim() && !e.playerTag.toLowerCase().includes(search.trim().toLowerCase())) return false;
    if (archetypeFilter && e.archetype?.name !== archetypeFilter) return false;
    return true;
  });

  return (
    <div className="container" style={{ paddingTop: 40, paddingBottom: 80 }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        style={{ marginBottom: 32 }}
      >
        <p style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
          Global Rankings
        </p>
        <h1 style={{ fontSize: 28, fontWeight: 900, letterSpacing: '-0.03em', marginBottom: 8 }}>Leaderboard</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          Top cognitive performers ranked by score. Updated after every completed combine.
        </p>
      </motion.div>

      {/* Controls */}
      <div className="leaderboard-controls" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24, alignItems: 'center' }}>
        {/* Genre tabs */}
        <div style={{ display: 'flex', gap: 2, background: 'var(--bg-elevated)', borderRadius: 10, padding: 3, border: '1px solid var(--border)' }}>
          {GENRES.map((g) => (
            <button
              key={g.id}
              onClick={() => setGenre(g.id)}
              style={{
                padding: '6px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 700, letterSpacing: '0.05em',
                background: genre === g.id ? 'var(--accent)' : 'transparent',
                color: genre === g.id ? '#000' : 'var(--text-muted)',
                transition: 'all 0.15s',
              }}
            >
              {g.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search player tag..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input nr-search-input"
          style={{ width: 200, fontSize: 13, padding: '7px 12px' }}
        />

        {/* Archetype filter */}
        {archetypes.length > 0 && (
          <select
            value={archetypeFilter}
            onChange={(e) => setArchetypeFilter(e.target.value)}
            style={{
              background: '#0d1117', border: '1px solid var(--border)',
              borderRadius: 8, color: archetypeFilter ? 'var(--accent)' : 'var(--text-primary)', fontSize: 13,
              padding: '6px 10px', cursor: 'pointer', fontWeight: 600,
              borderColor: archetypeFilter ? 'var(--accent)' : 'var(--border)',
            }}
          >
            <option value="">All Archetypes</option>
            {archetypes.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}

        {/* Sort by */}
        <div className="lb-sort" style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Rank by</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              background: '#0d1117', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text-primary)', fontSize: 13,
              padding: '6px 10px', cursor: 'pointer', fontWeight: 600,
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {error && <p style={{ color: 'var(--danger)', marginBottom: 16 }}>{error}</p>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)', fontSize: 14 }}>
          Loading rankings...
        </div>
      ) : entries.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 24px' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No profiles found for this filter. Be the first to complete a combine.</p>
          <Link to="/" className="btn btn--primary" style={{ display: 'inline-block', marginTop: 16 }}>Take the Combine →</Link>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {/* Table header */}
          <div className="leaderboard-header" style={{
            display: 'grid',
            gridTemplateColumns: '52px 1fr 190px 90px 110px',
            padding: '10px 20px',
            borderBottom: '1px solid var(--border)',
            fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}>
            <span style={{ textAlign: 'center' }}>#</span>
            <span>Player</span>
            <span className="lb-col-archetype">Archetype</span>
            <span className="lb-col-genre" style={{ textAlign: 'center' }}>Genre</span>
            <span style={{ textAlign: 'center' }}>{sortLabel}</span>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={`${genre}-${sortBy}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {filtered.map((entry, i) => {
                const rankColor = RANK_COLORS[entry.rank];
                const sortScore = entry.scores[sortBy];
                return (
                  <Link
                    key={entry.shareableId}
                    to={`/profile/${entry.shareableId}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <motion.div
                      className="leaderboard-row"
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.25, delay: Math.min(i * 0.03, 0.6) }}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '52px 1fr 190px 90px 110px',
                        padding: '13px 20px',
                        borderBottom: i < entries.length - 1 ? '1px solid var(--border)' : 'none',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'background 0.12s',
                        background: entry.rank <= 3 ? `${rankColor}06` : 'transparent',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = entry.rank <= 3 ? `${rankColor}06` : 'transparent'; }}
                    >
                      {/* Rank */}
                      <span style={{
                        fontSize: entry.rank <= 3 ? 15 : 13,
                        fontWeight: 900,
                        color: rankColor || 'var(--text-muted)',
                        fontVariantNumeric: 'tabular-nums',
                        textAlign: 'center',
                      }}>
                        {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : entry.rank}
                      </span>

                      {/* Player */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                          {entry.isPlayerPro && (
                            <svg width={12} height={12} viewBox="0 0 24 24" fill="#f59e0b" style={{ flexShrink: 0 }}>
                              <path d="M5 16L2 6l5 4 5-8 5 8 5-4-3 10H5z"/>
                            </svg>
                          )}
                          {entry.playerTag}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, fontFamily: 'var(--font-mono)' }}>
                          {entry.shareableId}
                        </div>
                      </div>

                      {/* Archetype */}
                      <div className="lb-col-archetype" style={{ overflow: 'hidden', minWidth: 0, paddingRight: 16 }}>
                        <ArchetypeBadge archetypeId={entry.archetype?.id} archetypeName={entry.archetype?.name} />
                      </div>

                      {/* Genre */}
                      <span className="lb-col-genre" style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'capitalize', fontWeight: 600, textAlign: 'center' }}>
                        {entry.genre}
                      </span>

                      {/* Score */}
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <ScoreBar value={sortScore} />
                      </div>
                    </motion.div>
                  </Link>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      {entries.length > 0 && (
        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', marginTop: 16 }}>
          {(search.trim() || archetypeFilter) ? `${filtered.length} of ${entries.length} profiles match` : `Top ${entries.length} profiles`} · Ranked by {sortLabel}
        </p>
      )}

      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <a
          href="https://discord.gg/W9Ftc6TC8J"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block', padding: '10px 24px', borderRadius: 10,
            fontSize: 13, fontWeight: 600, textDecoration: 'none',
            background: 'rgba(88,101,242,0.08)', border: '1px solid rgba(88,101,242,0.25)',
            color: '#5865F2',
          }}
        >
          Discuss results with other players → Join the Discord
        </a>
      </div>
    </div>
  );
}
